import React, { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { SEND_PREFILL_LINKS } from '../../../../graphql/mutation'
import { GET_USER_EMAIL_SIGNATURE } from '../../../../graphql/query'

const SIGNATURE_REQUIRED_MESSAGE = 'Please add signature'

type FromSender = 'botgo' | 'globtier'

interface Props {
  isOpen: boolean
  onClose: () => void
  recipientUuids: string[]
  onSent: () => void
  mode?: 'send' | 'resend'
}

const modalStyles: ReactModal.Styles = {
  overlay: { zIndex: 1060, backgroundColor: 'rgba(15, 23, 42, 0.45)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(440px, 92vw)',
    border: 'none',
    borderRadius: '14px',
    padding: '1.75rem',
    boxShadow: '0 20px 50px rgba(0,0,0,0.15)'
  }
}

const FROM_OPTIONS: Array<{ value: FromSender; label: string; email: string }> = [
  { value: 'botgo', label: 'Botgo', email: 'info@botgo.io' },
  { value: 'globtier', label: 'Globtier', email: 'HR.Updates@globtierinfotech.com' }
]

const SendPrefillLinksModal: React.FC<Props> = ({
  isOpen,
  onClose,
  recipientUuids,
  onSent,
  mode = 'send'
}) => {
  const [customMessage, setCustomMessage] = useState('')
  const [fromSender, setFromSender] = useState<FromSender>('botgo')
  const [loading, setLoading] = useState(false)

  const [sendLinks] = useMutation(SEND_PREFILL_LINKS, {
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } }
  })

  const { refetch: refetchSignature } = useQuery(GET_USER_EMAIL_SIGNATURE, {
    skip: !isOpen,
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (isOpen) {
      setCustomMessage('')
      setFromSender('botgo')
    }
  }, [isOpen])

  const handleSend = async () => {
    if (recipientUuids.length === 0) {
      toast.error('No recipients selected')
      return
    }

    const signatureResult = await refetchSignature()
    if (!signatureResult.data?.getUserEmailSignature?.hasSignature) {
      toast.error(SIGNATURE_REQUIRED_MESSAGE)
      return
    }

    setLoading(true)
    try {
      const { data } = await sendLinks({
        variables: {
          input: {
            recipientUuids,
            customMessage: customMessage || undefined,
            fromSender
          }
        }
      })
      const result = data?.sendPrefillLinks
      if (!result?.success) {
        toast.error(result?.message || 'Failed to queue emails')
        return
      }
      if (result.queued > 0) {
        toast.success(
          result.message ||
            `Queued ${result.queued} email(s). Delivery will continue in the background.`
        )
      } else {
        toast.success(result.message || 'Emails queued')
      }
      setCustomMessage('')
      onSent()
      onClose()
    } catch (err: any) {
      toast.error(err.message || 'Failed to send emails')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ReactModal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className='d-flex align-items-start justify-content-between mb-3'>
        <div>
          <h5 className='fw-semibold mb-1'>{mode === 'resend' ? 'Resend mail' : 'Send mail'}</h5>
          <p className='text-muted small mb-0'>
            {recipientUuids.length} recipient{recipientUuids.length === 1 ? '' : 's'} will be queued
            for email delivery (sent in the background).
          </p>
        </div>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>

      <div className='mb-3'>
        <label className='form-label small fw-medium text-muted mb-2'>Send from</label>
        <div className='d-flex flex-column gap-2'>
          {FROM_OPTIONS.map((option) => (
            <label
              key={option.value}
              className='d-flex align-items-start gap-2 p-2 rounded border'
              style={{
                cursor: 'pointer',
                borderColor: fromSender === option.value ? '#7c3aed' : '#e5e7eb',
                background: fromSender === option.value ? '#f5f3ff' : '#fff'
              }}
            >
              <input
                type='radio'
                className='mt-1'
                name='prefill-from-sender'
                checked={fromSender === option.value}
                onChange={() => setFromSender(option.value)}
              />
              <span>
                <span className='d-block fw-medium' style={{ fontSize: '0.875rem' }}>
                  {option.label}
                </span>
                <span className='text-muted' style={{ fontSize: '0.75rem' }}>
                  {option.email}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className='mb-4'>
        <label className='form-label small fw-medium text-muted'>Message (optional)</label>
        <textarea
          className='form-control'
          rows={4}
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value)}
          placeholder='Add a note to include in the email body...'
          style={{ borderRadius: '8px', fontSize: '0.875rem' }}
        />
      </div>

      <div className='d-flex justify-content-end gap-2'>
        <button type='button' className='btn btn-light btn-sm px-3' onClick={onClose}>
          Cancel
        </button>
        <button
          type='button'
          className='btn btn-primary btn-sm px-4'
          onClick={handleSend}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' />
              Sending...
            </>
          ) : (
            <>
              <i className={`bi ${mode === 'resend' ? 'bi-arrow-repeat' : 'bi-envelope'} me-1`} />
              {mode === 'resend' ? 'Resend mail' : 'Send mail'}
            </>
          )}
        </button>
      </div>
    </ReactModal>
  )
}

export default SendPrefillLinksModal
